import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const MauritiusPage = () => {
  const mauritiusData = {
    name: 'Mauritius',
    region: 'Indian Ocean / East Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Mauritius | MRA | CTD-Aligned Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Mauritius. MRA requirements, dossier format, labelling, key documents, and market opportunity in a high-NCD, import-dependent island market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Mauritius',
      subtitle: 'An upper-middle-income island market with high NCD burden, strong dependence on imports, and a structured national medicines authority.',
      cta: 'Start Your Mauritius Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~1.27M' },
      { label: 'Income Level', value: 'Upper-middle income' },
      { label: 'Health Expenditure (% of GDP)', value: '~6.4%' },
      { label: 'Health Expenditure per Capita', value: '~USD 591' },
      { label: 'Pharma Imports (2023)', value: '~USD 195M' },
      { label: 'Per-Capita Pharma Spend', value: 'Very high' }
    ],
    
    marketOverview: {
      title: 'Market Overview: High-Value, Highly Import-Dependent',
      stats: [
        'Mauritius imported MUR 8.5 billion (≈USD 195M) of pharmaceuticals in 2023',
        'For 1.27M population - very high per-capita medicine spend',
        'India is the single largest supplier (≈28% of pharma imports)',
        '386 retail drugstores and 33 wholesalers recorded',
        'Private wholesalers import about 75% of products',
        'Public sector: Government procurement via tenders (mostly generics)',
        'Private sector: Private hospitals and retail pharmacies (branded and higher-value products)',
        'High NCD burden drives strong demand for vitamins, metabolic support, weight management, heart health'
      ],
      opportunities: [
        'High per-capita pharma spend with sustained upward trend',
        'Strong acceptance of Indian generics and dossiers',
        'NCD-heavy market ideal for chronic therapies',
        'Nutraceuticals sold through pharmacies and supermarkets'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: NCD-Heavy, Post-Malaria Island',
      subtitle: 'Mauritius is different from many African markets: infectious disease burden is relatively low, while NCDs dominate morbidity and mortality.',
      categories: [
        {
          name: 'Non-Communicable Diseases (NCDs) — Market Driver',
          items: [
            'Diabetes: Leading cause of death, Type 2 diabetes prevalence ≈19.9% among adults 25-74 years',
            'Obesity: One in three adults is obese, ≈72% of population overweight or obese',
            'Cardiovascular disease: Major cause of mortality, thousands of CVD deaths yearly',
            'Pharma Portfolio: Antidiabetics, fixed-dose combos, antihypertensives, ACE inhibitors, ARBs, CCBs, statins, antiplatelets',
            'Nutra Portfolio: Metabolic & glycaemic control, cardiometabolic support (omega-3, CoQ10, ALA), weight management, antioxidants, liver support'
          ]
        },
        {
          name: 'Infectious Diseases — Relatively Low',
          items: [
            'Malaria: Certified malaria-free since 1973 (WHO)',
            'TB incidence: Relatively low ≈12 cases per 100,000 population',
            'Market Implication: NCD and lifestyle diseases drive long-term pharmaceutical and nutraceutical demand',
            'Chronic-centric market, not acute-infection dominant'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: Antidiabetics (mono + combinations), antihypertensives & cardiology, lipid-lowering & metabolic products, oncology and specialty',
            'Nutra: Glycaemic control & insulin sensitivity, cardiometabolic formulations, weight management support, antioxidants, women\'s health (bone, hormonal balance)'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Medicines Regulatory Authority (MRA)',
      description: 'Semi-autonomous agency under the Ministry of Health and Wellness, responsible for marketing authorization, inspection, import control, licensing, pharmacovigilance. Pharmacy Board (established under Pharmacy Act 1983) controls medicines, inspections, and licensing. Dossier structure is CTD-aligned (ICH-style).',
      pharma: {
        timeline: 'Varies based on dossier completeness, pricing review, and MRA evaluation',
        requirements: [
          'Local company (importer/wholesaler) acts as Marketing Authorization Holder',
          'CTD-aligned dossier: Administrative, quality, safety and efficacy modules',
          'Valid GMP certificate and evidence of registration in country of origin',
          'Quality section: API info, finished product specs, manufacturing process, stability data',
          'Evaluation includes quality review, pricing considerations, sample testing',
          'Government operates price control framework with fixed margins',
          'Registrations require renewal; MRA oversees post-marketing surveillance'
        ],
        insight: 'Mauritius uses ICH-CTD-aligned structure similar to other African NRAs. Indian GMP is acceptable when properly documented. Products can be sold through public tenders or private wholesalers once approved.'
      },
      nutra: {
        timeline: 'Depends on classification under food/dietary product framework',
        requirements: [
          'Regulated under Food Act 1998 and Food Regulations 1999 (updated with Food Act 2022)',
          'Nutrition and dietary labelling requirements for nutrients and claims',
          'Special labelling for dietary products: composition, nutrient disclosure, claims limitations',
          'Classified as pre-packed foods/dietary products with label compliance review',
          'Accurate labelling emphasized in newer draft regulations'
        ],
        insight: 'Many nutraceuticals are marketed as pre-packed foods/dietary products. Label review and compliance services commonly used to ensure compliance with national rules.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in Mauritius (CTD-Aligned)',
      steps: [
        'Local Representative / MAH - Local company (importer/wholesaler) acts as Marketing Authorization Holder',
        'Dossier Submission - CTD-aligned: Administrative, quality, safety and efficacy sections',
        'GMP & Product Registration - Valid GMP certificate and evidence of registration in country of origin',
        'Quality documentation - API info, finished product specs, manufacturing process, stability data',
        'Evaluation & Queries - Quality review, pricing considerations, occasional sample testing',
        'Approval & Pricing - Products registered, price control framework and fixed margins apply',
        'Renewals & Pharmacovigilance - Registration renewal required, post-marketing surveillance'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Food Supplement Registration',
      steps: [
        'Classification under Food Act 1998 and Food Regulations 1999 (Food Act 2022 framework)',
        'Full qualitative & quantitative composition preparation',
        'COA for finished product and key ingredients',
        'Manufacturer GMP / quality certificates',
        'Evidence of safety for novel or high-dose components',
        'Labelling artwork aligned to Food Regulations (ingredients, nutrition, claims)',
        'Claims justification for specific nutrition/health claims',
        'Label compliance review (often via certification bodies)',
        'Regulatory approval for pre-packed food/dietary product classification',
        'Market entry through pharmacies and supermarkets'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals': [
        'Application form and administrative documents (local MAH details, licences)',
        'CTD-style dossier: Quality/chemistry (API & finished product)',
        'Non-clinical / clinical summaries (where needed)',
        'Manufacturing licence & GMP certificate',
        'Evidence of registration / authorization in country of origin',
        'Finished product specifications & COA',
        'Stability data (Zone IV/IVb for tropical climate)',
        'Pack inserts, labels and SPC',
        'Pricing and margin data (for certain products)'
      ],
      'Nutraceuticals / Food Supplements': [
        'Full qualitative & quantitative composition',
        'COA (finished product + key ingredients)',
        'Manufacturer GMP / quality certificates',
        'Evidence of safety for novel or high-dose components',
        'Labelling artwork aligned to Food Regulations 1999 + updates',
        'Ingredients, nutrition, claims documentation',
        'Claims justification for nutrition/health claims'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Mauritius Should Be on Your Expansion Map',
      reasons: [
        'High per-capita pharma spend: USD ~195M imports for 1.27M people',
        'Strong NCD burden (≈20% diabetes, 72% overweight/obese) → sustained chronic therapy demand',
        'Malaria eliminated, TB low → chronic-centric market, not acute-infection dominant',
        'India is already top supplier, proving strong acceptance of Indian generics',
        'Small but high-value, margin-conscious market → ideal for curated, higher-ASP products',
        'Upper-middle income economy with purchasing power',
        '386 retail drugstores and established wholesale network',
        'Government tenders and private sector opportunities'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics (mono + combinations), antihypertensives & cardiology, lipid-lowering & metabolic, specialty products',
        'Nutra: Glycaemic control, cardiometabolic formulations (omega-3, CoQ10, ALA), weight management, antioxidants, liver support, women\'s health'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Mauritius',
      services: [
        'Market & Portfolio Fit - Match products with Mauritius\' NCD profile and tender + private mix',
        'CTD-Aligned Dossier Preparation - Build/adapt CTD-style dossiers for MRA requirements',
        'Nutra Classification & Documentation - Position under appropriate Food/Dietary Product framework',
        'Label & Artwork Compliance - Align with Food Regulations 1999 + new drafts',
        'Local Partner Coordination - Engage wholesalers/importers who can act as MAH',
        'Query, Pricing & Lifecycle Support - Handle MRA queries, pricing documentation, renewals, pharmacovigilance'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Mauritius?',
        answer: 'The Medicines Regulatory Authority (MRA), a semi-autonomous agency under the Ministry of Health and Wellness, is responsible for marketing authorization, inspection, import control and pharmacovigilance.'
      },
      {
        question: 'Does Mauritius require CTD format for pharma dossiers?',
        answer: 'Yes. Mauritius has formal registration guidelines and uses an ICH-CTD-aligned structure, with administrative, quality, safety and efficacy sections similar to other African NRAs.'
      },
      {
        question: 'Is Indian GMP accepted?',
        answer: 'Yes. Tender documents indicate that the product should be manufactured and registered with the regulatory authority in the country of origin, and Indian GMP is acceptable when properly documented.'
      },
      {
        question: 'How are nutraceuticals regulated?',
        answer: 'Nutraceuticals are typically regulated as pre-packaged foods/dietary products under the Food Act and Food Regulations, with specific labelling and nutrition requirements.'
      },
      {
        question: 'Is Mauritius more of an NCD or infectious-disease market?',
        answer: 'Predominantly NCD-driven. Diabetes, obesity and cardiovascular diseases dominate the health burden, while malaria has been eliminated and TB incidence is relatively low.'
      }
    ]
  };

  return <CountryTemplate countryData={mauritiusData} />;
};
